import { FunctionComponent, useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styles from "./CheckoutPayment.module.scss";
import { MultiStepProgress } from "../../components/MultiStepProgress";
import { FormSelect as Select } from "../../components/basic/FormSelect";
import { RadioComponent as Radio } from "../../components/basic/Radio";
import { CompoundSlider as Slider } from "components/basic/Slider";
import { FormInput as TextField } from "../../components/basic/FormInput";
import { CheckboxComponent as Checkbox } from "../../components/basic/Checkbox";
import { getApplePayAvailability } from "../../core/applePay";
import { GOOGLE_PAY_ENVIRONMENT, GOOGLE_PAY_LIVE_FOR_ALL, loadGooglePaySdk } from "../../core/googlePay";
import { GooglePayButton } from "../../components/GooglePayButton/GooglePayButton";
import googlePayMark from "../../assets/img/google-pay-mark.svg";
import { checkCardType, cardNumberFormat } from "../../core/helpers";
import amexCard from "../../assets/img/amex_card.png";
import maestroCard from "../../assets/img/maestro_card.png";
import masterCard from "../../assets/img/master_card.png";
import visaCard from "../../assets/img/visa_card.png";
import { Spinner } from "components/basic/Spinner";
import { V12Calculator } from "@/pages/Finance/V12Calculator";
import {
  fetchPaymentReviewDetail,
  selectFormArr,
  selectRegistrationDetail,
  selectFinanceOptions,
  selectFinancePrice,
  submitFinanceOption,
  selectCreditInformation,
  submitCheckoutPayment,
  // selectDekoOption,
  setCardTypeText,
  // setLoanDeposit,
  selectFetchingStatus,
  selectForceDepositMax,
  selectMaximumDeposit,
  clearThreeDSAuthData,
  setThreeDSAuthData,
  selectIsSubmitting,
  selectCalcPrice,
  selectPlateFixtureValue,
  selectPlateFixture,
  selectDiscountAmount,
} from "./checkoutPaymentSlice";
import { ButtonRound } from "components/basic/Buttons";
import {
  selectFormValues,
  initializeFormId,
} from "../CheckoutInfo/checkoutInfoSlice";
import { checkoutPaymentClient } from "../../core/api";
import { useAppSelector, useAppDispatch } from "../../app/hooks";
import { selectIsAuthenticate, selectUserInfo } from "@/core/store/authSlice";
import { PriceBreakdown } from "./PriceBreakdown/PriceBreakdown";
import { Criteria } from "./Criteria/Criteria";
import {
  progressInfosFactory,
  fontKeys,
  logoKeys,
  borderKeys,
  sizeKeys,
} from "../checkout/constants/index";
import { ThreeDAuthDialog } from "@/components/ThreeDAuthDialog/ThreeDAuthDialog";
import { trackAddPaymentInfo, trackPurchase, trackPaymentMethodSelect, trackPaymentDeclined } from "../../core/helpers/tracking";

type Props = {};

const nf = new Intl.NumberFormat("en", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const POUND = "\u00A3";

const appellation = ["Mr", "Miss", "Mrs", "Ms"];
const cardTypeText: { [key: string]: string } = {
  AmexCard: "American Express",
  VisaCard: "Visa",
  MasterCard: "Mastercard",
  MaestroCard: "Maestro",
};

// New Reg's Opayo account does not take American Express: every Amex attempt
// is refused 3069 (card) or 6403 (wallet), e.g. X31 PSB on 14 Oct 2026. So the
// card form stops an Amex number before it is sent, and the wallets do not
// offer Amex cards.
const NO_AMEX = "We don't take American Express. Please use a Visa or Mastercard.";

const CURRENT_PAGE_NUMBER = 2;
// Wallet payments. Apple Pay runs on the Opayo (Elavon) Direct rail; the old
// Worldpay route was dropped 2026-08-11 and its code deleted 2026-09-28. Enabled on beta ONLY, by explicit
// hostname allow-list rather than "not www" — an allow-list cannot leak onto an
// unexpected host. Do not switch this to a blanket true until the charge half is
// proven end to end.
//
// LIVE-TEST OPT-IN, added 2026-09-30. Elavon confirmed wallets are NOT functional
// in the test environment at all — "any testing using the Opayo gateway must be
// performed using the live environment" — so the charge can only ever be proven
// on www. That created a deadlock with the note above: do not go live until the
// charge is proven, and the charge cannot be proven until it is live.
//
// This breaks the deadlock without exposing an unproven payment path to real
// traffic. On www the wallets stay OFF for everyone except a session that has
// explicitly opted in with ?wallet=test. The opt-in is held in sessionStorage
// because the checkout is an SPA and this flag is read again on a later step; it
// dies with the tab.
//
// It is still an ALLOW-LIST — the host must be exactly beta or www — so it
// cannot leak onto an unexpected hostname.
//
// WHEN THE CHARGE IS PROVEN: delete the opt-in branch and return true for www.
// Do not leave the query-string route in place once wallets are properly live.
const WALLET_PAY_ENABLED = (() => {
  if (typeof window === 'undefined') { return false; }
  const host = window.location.hostname;
  if (host === 'beta.newreg.co.uk') { return true; }
  if (host !== 'www.newreg.co.uk') { return false; }
  try {
    if (new URLSearchParams(window.location.search).get('wallet') === 'test') {
      sessionStorage.setItem('nrWalletLiveTest', '1');
    }
    return sessionStorage.getItem('nrWalletLiveTest') === '1';
  } catch (e) {
    // Private mode or blocked storage — fail closed, wallets stay hidden.
    return false;
  }
})();
// Google Pay is configured at Opayo (gateway id opayoelavon, gateway merchant
// id issued 2026-09-25), so the button can be exercised on beta. The earlier
// note here said it was off for want of a merchant id and because the
// tokenizationSpecification still named Worldpay - both untrue since 2026-09-25.
// GOOGLE_PAY_ENVIRONMENT (core/googlePay.ts) is PRODUCTION on www.newreg.co.uk
// only (Google approved 2026-10-06) and TEST everywhere else, so beta still
// gets dummy tokens and its charge fails by design (no wallet entitlement on
// the Opayo sandbox).
// APPLE PAY LIVE on www for every customer, 2026-10-02 (Jon: "lets get apple pay
// deployed"). Proven on production (P2XYP1790943374) with the price floor and
// discount re-validation live. GOOGLE PAY LIVE on www for every customer,
// 2026-10-06, via GOOGLE_PAY_LIVE_FOR_ALL (core/googlePay.ts). MUST STAY
// BYTE-IDENTICAL in CheckoutInfo.tsx and CheckoutPayment.tsx. Still a host allow-list.
const APPLE_PAY_ENABLED: boolean = WALLET_PAY_ENABLED ||
  (typeof window !== 'undefined' && window.location.hostname === 'www.newreg.co.uk');
const GOOGLE_PAY_ENABLED: boolean = WALLET_PAY_ENABLED || GOOGLE_PAY_LIVE_FOR_ALL;
// GOOGLE_PAY_ENVIRONMENT now lives in core/googlePay.ts, shared with CheckoutInfo.

const CheckoutPayment: FunctionComponent<Props> = (props: Props) => {
  const dispatch = useAppDispatch();
  const [applePayAvailable, setApplePayAvailable] = useState(false);
  const [isApplePaySubmitting, setIsApplePaySubmitting] = useState(false);
  const [googlePayAvailable, setGooglePayAvailable] = useState(false);
  const [isGooglePaySubmitting, setIsGooglePaySubmitting] = useState(false);
  const [walletError, setWalletError] = useState('');
  const pendingWalletRef = useRef('');
  const redirectAllowedRef = useRef(false);

  // Apple Pay availability. Goes through core/applePay, which loads Apple's JS
  // SDK so this works in Chrome/Edge/Firefox as well as Safari, and reports
  // whether a card is actually provisioned in Wallet.
  useEffect(() => {
    if (!APPLE_PAY_ENABLED) return;
    let cancelled = false;
    getApplePayAvailability().then(({ available, hasCard }) => {
      if (cancelled || !available) return;
      setApplePayAvailable(true);
      // Primary payment option when a card is provisioned — Apple's Acceptable
      // Use Guidelines require it once applePayCapabilities has been consulted,
      // and Jon confirmed he wants it. An explicit choice carried over from
      // step 1 still wins, and we never override a method the user has picked.
      if (pendingWalletRef.current === 'applepay') {
        setPayMethod('ApplePay');
      } else if (hasCard && !pendingWalletRef.current) {
        setPayMethod('ApplePay');
      }
    });
    return () => { cancelled = true; };
  }, []);

  // Load Google Pay JS and check availability (bypass isReadyToPay — returns false on Chrome without saved card)
  useEffect(() => {
    if (!GOOGLE_PAY_ENABLED) return;
    loadGooglePaySdk().then((ok) => {
      if (!ok) return;
      setGooglePayAvailable(true);
      if (pendingWalletRef.current === 'googlepay') setPayMethod('GooglePay');
    });
  }, []);
  // Google Pay handler
  const handleGooglePay = async () => {
    setWalletError('');
    if (!(window as any).google?.payments?.api) return;
    const client = new (window as any).google.payments.api.PaymentsClient({ environment: GOOGLE_PAY_ENVIRONMENT });
    const totalAmount = Math.max(
      0,
      (calcPrice || 0) + (plateFixtureValue || 0) - (discountAmount || 0)
    ).toFixed(2);
    const currentFormId = formId || "";
    try {
      setIsGooglePaySubmitting(true);
      const paymentData: any = await client.loadPaymentData({
        apiVersion: 2,
        apiVersionMinor: 0,
        allowedPaymentMethods: [{
          type: "CARD",
          parameters: {
            allowedAuthMethods: ["PAN_ONLY", "CRYPTOGRAM_3DS"],
            allowedCardNetworks: ["MASTERCARD", "VISA"],
          },
          tokenizationSpecification: {
            type: "PAYMENT_GATEWAY",
            // Opayo (Elavon), not Worldpay. gateway string and gatewayMerchantId
            // both come from MyOpayo > Settings > Pay Methods > Google Pay;
            // confirmed there 2026-09-25. gatewayMerchantId is Opayo's id for us
            // and is NOT the Google merchant id below — they are different things
            // and swapping them silently breaks tokenisation.
            parameters: {
              gateway: "opayoelavon",
              gatewayMerchantId: "f02a57e7-1a87-40fc-9b5b-b89cbdfd730a",
            },
          },
        }],
        merchantInfo: { merchantId: "BCR2DN5TQ2H6LBDU", merchantName: "New Reg Limited" },
        transactionInfo: {
          totalPriceStatus: "FINAL",
          totalPriceLabel: "Total",
          totalPrice: totalAmount,
          currencyCode: "GBP",
          countryCode: "GB",
        },
      });
      const walletToken = paymentData.paymentMethodData.tokenizationData.token;
      trackAddPaymentInfo(registration, parseFloat(totalAmount), 'google_pay');
      const result: any = await checkoutPaymentClient.submitGooglePayCharge(currentFormId, walletToken, totalAmount, plateFixture || "", plateFixtureValue || 0);
      if (result?.data?.status === "success") { trackPurchase(registration, parseFloat(totalAmount), 'google_pay', undefined, (userInfo?.user as string) || undefined); nextPage(); }
      else if (result?.data?.status === "3DAUTH") {
        // PAN_ONLY card (saved in the shopper's Google account): the bank wants
        // 3-D Secure. Same dialog as the card checkout; on success it calls
        // nextPage() itself.
        dispatch(setThreeDSAuthData(result.data));
      }
      else { trackPaymentDeclined(registration, parseFloat(totalAmount), 'google_pay'); setWalletError('Payment not completed.'); }
    } catch (err: any) {
      if (err?.statusCode === "CANCELED") return;
      const gpMsg = (err as any)?.error?.messages?.common?.[0] || (err as any)?.message || JSON.stringify(err);
      trackPaymentDeclined(registration, parseFloat(totalAmount), 'google_pay', gpMsg || 'exception');
      setWalletError(gpMsg || 'Google Pay failed.');
      console.error("[NR Google Pay] Payment failed:", err);
    } finally {
      setIsGooglePaySubmitting(false);
    }
  };

  // Read pre-selected wallet from sessionStorage on mount
  useEffect(() => {
    const method = sessionStorage.getItem('nrWalletMethod') || '';
    if (method) {
      pendingWalletRef.current = method;
      sessionStorage.removeItem('nrWalletMethod');
    }
  }, []);

  // Restore order preference answers saved on CheckoutCustomise
  useEffect(() => {
    const tp = sessionStorage.getItem('nrThirdParty');
    const pe = sessionStorage.getItem('nrPlainEnvelope');
    if (tp !== null) { setThirdPartyQualify(tp === '1'); sessionStorage.removeItem('nrThirdParty'); }
    if (pe !== null) { setSendCommunciation(pe === '1'); sessionStorage.removeItem('nrPlainEnvelope'); }
  }, []);

  // Apple Pay handler — called on button click
  const handleApplePay = () => {
    if (!(window as any).ApplePaySession) return;

    const paymentRequest = {
      countryCode: "GB",
      currencyCode: "GBP",
      supportedNetworks: ["visa", "masterCard"],
      merchantCapabilities: ["supports3DS"],
      total: {
        label: "New Reg",
        amount: Math.max(
          0,
          (calcPrice || 0) + (plateFixtureValue || 0) - (discountAmount || 0)
        ).toFixed(2),
      },
    };

    const session = new (window as any).ApplePaySession(3, paymentRequest);

    session.onvalidatemerchant = async (event: any) => {
      try {
        const response: any = await checkoutPaymentClient
          .validateAppleMerchant(event.validationURL, window.location.hostname, formId || "");
        // The API wraps every reply as { data: ... } and the axios interceptor
        // in core/api/base/http.ts already returns the body, so what arrives
        // here is { data: <session> }. Apple needs the BARE session object and
        // rejects the wrapper — unwrap defensively so it works either way.
        const merchantSession = response && response.data ? response.data : response;
        session.completeMerchantValidation(merchantSession);
      } catch (err: any) {
        console.error("[NR Apple Pay] Merchant validation failed:", err);
        session.abort();
      }
    };

    session.onpaymentauthorized = async (event: any) => {
      setIsApplePaySubmitting(true);
      // SEND THE WHOLE event.payment, NOT token.paymentData.
      //
      // Opayo want the object Apple hands to onpaymentauthorized in full, whose
      // top level is { token, billingContact, shippingContact }. Elavon's own
      // sample passes event.payment straight through, and their documented
      // payload has `token` as a KEY with paymentData nested inside it.
      //
      // Sending token.paymentData is two levels too deep: the JSON becomes
      // { version, data, signature, header } with no `token` anywhere, and the
      // gateway refuses with
      //     6149 : Invalid payload. Token element not provided.
      // Proven against live Opayo on 2026-09-30 and fixed on the partner page,
      // which then took a real payment. This path had the identical defect and
      // would have failed on its first real customer — it had simply never been
      // exercised, because a wallet charge cannot be tested outside live.
      const walletToken = JSON.stringify(event.payment || {});
      // Apple's network/type, which the server turns into Opayo's CardType.
      const paymentMethod = (event.payment && event.payment.token && event.payment.token.paymentMethod) || {};
      const currentFormId = formId || "";
      trackAddPaymentInfo(registration, parseFloat(paymentRequest.total.amount), 'apple_pay');
      try {
        const result: any = await checkoutPaymentClient
          .submitApplePayCharge(
            currentFormId,
            walletToken,
            String((paymentRequest.total.amount)),
            paymentMethod.network || "",
            paymentMethod.type || "",
            plateFixture || "",
            plateFixtureValue || 0
          );
        if (result?.data?.status === "success") {
          session.completePayment((window as any).ApplePaySession.STATUS_SUCCESS);
          trackPurchase(registration, parseFloat(paymentRequest.total.amount), 'apple_pay', undefined, (userInfo?.user as string) || undefined);
          nextPage();
        } else {
          session.completePayment((window as any).ApplePaySession.STATUS_FAILURE);
          trackPaymentDeclined(registration, parseFloat(paymentRequest.total.amount), 'apple_pay');
          setWalletError('Apple Pay failed.');
        }
      } catch (err: any) {
        const apMsg = (err as any)?.error?.messages?.common?.[0] || (err as any)?.message || JSON.stringify(err);
        trackPaymentDeclined(registration, parseFloat(paymentRequest.total.amount), 'apple_pay', apMsg || 'exception');
        setWalletError(apMsg || 'Apple Pay failed.');
        console.error("[NR Apple Pay] Charge failed:", err);
        session.completePayment((window as any).ApplePaySession.STATUS_FAILURE);
      } finally {
        setIsApplePaySubmitting(false);
      }
    };

    session.oncancel = () => {
      console.log("[NR Apple Pay] Session cancelled");
      setIsApplePaySubmitting(false);
    };

    session.begin();
  };


  const { registration }: { registration: string } = useParams() as {
    registration: string;
  };

  const [sliderDomain, setSliderDomain] = useState<[number, number]>([0, 50]);
  const registrationDetail = useAppSelector(selectRegistrationDetail);
  const formArr = useAppSelector(selectFormArr);
  const financeOptions = useAppSelector(selectFinanceOptions);
  const financePrice = useAppSelector(selectFinancePrice);
  const creditInformation = useAppSelector(selectCreditInformation);
  const formValue = useAppSelector(selectFormValues);
  const fetchingStatus = useAppSelector(selectFetchingStatus);
  const maximumDeposit = useAppSelector(selectMaximumDeposit);
  const forceDepositMax = useAppSelector(selectForceDepositMax);
  const isSubmitting = useAppSelector(selectIsSubmitting);
  const userInfo     = useAppSelector(selectUserInfo);
  const isAuthenticate = useAppSelector(selectIsAuthenticate);
  const calcPrice = useAppSelector(selectCalcPrice);
  const plateFixtureValue = useAppSelector(selectPlateFixtureValue);
  const plateFixture = useAppSelector(selectPlateFixture);
  // The wallet sheets must bill the SAME figure the customer was shown.
  // applies_to ('total' | 'plates' | 'transfer_fee' | 'transfer_service') only
  // decides which line item displays the reduction — the total always drops by
  // exactly discountAmount, which is why one subtraction covers every code type.
  const discountAmount = useAppSelector(selectDiscountAmount);
  const formId = formValue?.formId;

  const [payMethod, setPayMethod] = useState("Card");
  const [_depositAmount, setDepositAmount] = useState(25);
  const [flipped, setFlipped] = useState(false);
  const [agreePolicy, setAgreePolicy] = useState(false);
  const [keepUpdate, setKeepUpdate] = useState(false);
  const [dateExpiried, setDateExpired] = useState(false);
  const [cardType, setCardType] = useState("");
  const [cardIcon, setCardIcon] = useState("");
  const [cardBgColor, setCardColor] = useState("");
  const [skipFinanceRecalculation, setSkipFinanceRecalculation] = useState(false);
  const [regPrev, setRegPrev] = useState(
    "https://www.newreg.co.uk/reg.php?reg=" +
      registration +
      "&logo=no_logo&border=border_no"
  );
  const [regPrevFront, setPrevFront] = useState("regular-front");
  const [regPrevBack, setPrevBack] = useState("regular-back");
  const [showFinance, setShowFinance] = useState(false);
  const [cardUseType, setCardUseType] = useState("Credit");

  // finance options
  const [value, setValue] = useState(30);
  const [options, setOption] = useState<Array<string>>([]);
  const [optionKeys, setOptionKeys] = useState<Array<string>>([]);
  const [optionValue, setOptionValue] = useState("");
  const [currentOption, setCurrentOption] = useState<{
    title: string;
    group: string;
    name: string;
    term: string;
    interest: number;
  }>();
  const [selectedFinanceOption, setSelectedFinanceOption] = useState('');

  // payment info
  const [appel, setAppel] = useState("Mr");
  const [firstName, setFirstName] = useState("");
  const [surName, setSurName] = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const [expiry, setExpiry] = useState("");
  const [cvv, setCvv] = useState("");

  // others
  const [thirdPartyQualify, setThirdPartyQualify] = useState(false);
  const [sendCommunciation, setSendCommunciation] = useState(false);
  const [redirectAllowed, setRedirectAllowed] = useState(false);

  const progressInfos = useMemo(
    () => progressInfosFactory({ registration }),
    [registration]
  );
  const progress = useMemo(
    () => progressInfos.map((info) => info.title),
    [progressInfos]
  );

  const navigate = useNavigate();
  const [calculator] = useState(() => new V12Calculator());
  const prevPage = () => {
    // keep query params in the url
    const urlParams = new URLSearchParams(window.location.search);
    const queryString = urlParams.toString();
    const prevPath = progressInfos[CURRENT_PAGE_NUMBER - 1].path;
    
    // if there is query params, append to the path
    navigate(queryString ? `${prevPath}?${queryString}` : prevPath);
  };
  const nextPage = (v12Url?: string) => {
    redirectAllowedRef.current = true;
    const currentFormId = formId || '';
    sessionStorage?.removeItem("formId");
    sessionStorage?.removeItem("formRegistration");
    sessionStorage?.removeItem("selectedFinanceDetails");
    let dest = progressInfos[CURRENT_PAGE_NUMBER + 1].path;
    if (currentFormId) dest += '&formId=' + encodeURIComponent(currentFormId);
    if (v12Url) dest += '&v12url=' + encodeURIComponent(v12Url);
    window.location.href = dest;
  };

  useEffect(() => {
    dispatch(initializeFormId());
    const urlParams = new URLSearchParams(window.location.search);
    setPayMethod(urlParams.get("finance") === "true" ? "Finance" : "Card");
    
    document.title =
      "New Reg Personalised Number Plates - buy private, cherished and DVLA number plates";
    return () => {
      dispatch(clearThreeDSAuthData());
    };
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Step 3 — fires on payment page mount (user submitted customise and reached payment)
  // Gap 2→3 = entered details but didn't proceed to payment
  // Gap 3→4 = reached payment but didn't complete sale
  // Dedup ref prevents double-fire when formValue.email loads after initial mount
  const _step3CapturedRef = useRef("");
  useEffect(() => {
    const email = formValue?.email || (userInfo?.user as string) || "";
    if (!registration || !email) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;
    const captureKey = registration + ":" + email;
    if (_step3CapturedRef.current === captureKey) return;
    _step3CapturedRef.current = captureKey;
    const body = new URLSearchParams({
      action:           "capture_checkout",
      registration:     registration,
      email:            email,
      oracle_user_id:   userInfo?.user_cust_id || "",
      is_authenticated: isAuthenticate ? "true" : "false",
      step:             "3",
    });
    fetch("/ajax.php", {
      method:    "POST",
      headers:   { "Content-Type": "application/x-www-form-urlencoded" },
      body:      body.toString(),
      keepalive: true,
    }).catch(() => {});
  }, [registration, formValue?.email, userInfo]);

  useEffect(() => {
    if (formId) {
      dispatch(
        fetchPaymentReviewDetail({
          registration,
          form_id: formId,
        })
      );
    }
  }, [formId]);

  useEffect(() => {
    if (cardType === "AmexCard") {
      setCardIcon(amexCard);
      setCardColor("#108168");
    }
    if (cardType === "VisaCard") {
      setCardIcon(visaCard);
      setCardColor("#191278");
    }
    if (cardType === "MasterCard") {
      setCardIcon(masterCard);
      setCardColor("#0061A8");
    }
    if (cardType === "MaestroCard") {
      setCardIcon(maestroCard);
      setCardColor("#0B2C5F");
    }
    if (!cardType) {
      setCardIcon("");
      setCardColor("");
    }
  }, [cardType]);

  useEffect(() => {
    let cardTypeMatchText = "";
    if (
      ["MasterCard", "VisaCard"].includes(cardType) &&
      cardUseType === "Debit"
    ) {
      if (cardType === "VisaCard") {
        cardTypeMatchText = "Delta";
      } else {
        cardTypeMatchText = "Mastercard Debit";
      }
    } else {
      cardTypeMatchText = cardTypeText[cardType];
    }
    dispatch(setCardTypeText(cardTypeMatchText));
  }, [cardType, cardUseType]);

  useEffect(() => {
    if (formArr) {
      setAppel(formArr.title || "Mr");
      setFirstName(formArr.forename);
      setSurName(formArr.surname);
      // reg preview
      let regPreview =
        "https://" +
        window.location.host +
        "/reg.php?reg=" +
        registration +
        "&font=" +
        (fontKeys[formArr.digit_style] ? fontKeys[formArr.digit_style] : "") +
        "&border=" +
        borderKeys[formArr.border];
      regPreview = formArr.euro
        ? regPreview + "&logo=" + logoKeys[formArr.euro]
        : regPreview + "&logo=no_logo";
      setRegPrev(regPreview);

      switch (sizeKeys[formArr.plate_size]) {
        case "regular":
          setPrevFront("regular-front");
          setPrevBack("regular-back");
          break;
        case "large":
          setPrevFront("regular-front");
          setPrevBack("large-back");
          break;
        case "square":
          setPrevFront("regular-front");
          setPrevBack("square-back");
          break;
        case "motorbike":
          setPrevFront("motorbike-back");
          setPrevBack("");
          break;
        default:
          setPrevFront("regular-front");
          setPrevBack("regular-back");
          break;
      }

      setShowFinance(
        ["1", "134", "57", "38"].indexOf(formArr.sourcecode) !== -1
      );
    }
  }, [formArr]);

  // calculate slider domain
  const calculateSliderDomain = (key: string): [number, number] => {
    if (!financeOptions[key] || !financePrice) return [0, 50];
    
    const option = financeOptions[key];
    const minLoan = typeof option.minLoan === 'string' ? parseFloat(option.minLoan) : option.minLoan;
    const maxLoan = typeof option.maxLoan === 'string' ? parseFloat(option.maxLoan) : option.maxLoan;
    
    // calculate min deposit percent
    let minDepositPercent = (financePrice - maxLoan) / financePrice * 100;
    minDepositPercent = Math.max(0, minDepositPercent);
    
    // calculate max deposit percent
    let maxDepositPercent = (financePrice - minLoan) / financePrice * 100;
    maxDepositPercent = Math.min(100, maxDepositPercent);
    
    // consider maximumDeposit limit
    maxDepositPercent = Math.min(maxDepositPercent, maximumDeposit);
    
    // ensure minDepositPercent <= maxDepositPercent
    if (minDepositPercent > maxDepositPercent) {
      return [0, maximumDeposit > 0 ? Math.floor(maximumDeposit) : 0];
    }
    
    // since the slider is integer step, we need to convert the percentage value to integer
    // for min value, ceil, ensure Credit Amount not exceed maxLoan
    // for max value, floor, ensure Credit Amount not less than minLoan
    
    // calculate min deposit percent
    const ceiledMinDepositPercent = Math.ceil(minDepositPercent);
    
    // check if the min deposit percent is less than maxLoan
    const depositAmountForCeiledMin = (financePrice / 100) * ceiledMinDepositPercent;
    const creditAmountForCeiledMin = financePrice - depositAmountForCeiledMin;
    
    // if the min deposit percent is less than maxLoan, use the original min deposit percent
    let finalMinDepositPercent = creditAmountForCeiledMin <= maxLoan ? ceiledMinDepositPercent : Math.floor(minDepositPercent);
    
    // calculate max deposit percent
    const flooredMaxDepositPercent = Math.floor(maxDepositPercent);
    
    // check if the max deposit percent is less than minLoan
    const depositAmountForFlooredMax = (financePrice / 100) * flooredMaxDepositPercent;
    const creditAmountForFlooredMax = financePrice - depositAmountForFlooredMax;
    
    // if the max deposit percent is less than minLoan, adjust the max deposit percent
    let finalMaxDepositPercent = flooredMaxDepositPercent;
    if (creditAmountForFlooredMax < minLoan) {
      // recalculate to ensure Credit Amount equals minLoan
      const adjustedMaxDepositPercent = ((financePrice - minLoan) / financePrice) * 100;
      // floor, ensure Credit Amount not less than minLoan
      finalMaxDepositPercent = Math.floor(adjustedMaxDepositPercent);
    }
    
    // ensure max deposit percent not exceed 50% （v12 default max deposit percent）
    finalMaxDepositPercent = Math.min(finalMaxDepositPercent, 50);

    // ensure min value not greater than max value
    if (finalMinDepositPercent > finalMaxDepositPercent) {
      finalMinDepositPercent = finalMaxDepositPercent;
    }
    
    return [finalMinDepositPercent, finalMaxDepositPercent];
  };

  useEffect(() => {
    const arr: Array<string> = [];
    const arr2: Array<string> = [];
    
    // Filter finance products that meet loan amount requirements
    const validFinanceOptions: { [key: string]: any } = {};
    let hasValidOption = false;
    
    for (let key in financeOptions) {
      const option = financeOptions[key];
      const actualLoanAmount = financePrice;
      
      if (actualLoanAmount >= option.minLoan && actualLoanAmount <= option.maxLoan && (parseFloat(option.interest.toString()) !== 0 || (parseInt(option.term) === 6 && registrationDetail?.sourcecode === "1" && registrationDetail?.base_price === 250))) {
        validFinanceOptions[key] = option;
        arr.unshift(option.name);
        arr2.unshift(key);
        hasValidOption = true;
      }
    }

    if (!hasValidOption && Object.keys(financeOptions).length > 0) {
      // if no valid finance options, hide finance
      setShowFinance(false);
      return;
    }
    
    setOption(arr);
    setOptionKeys(arr2);

    // Check URL parameters to determine if it's a finance path
    const urlParams = new URLSearchParams(window.location.search);
    const isFinancePath = urlParams.get("finance") === "true";

    // Try to get saved finance option from sessionStorage
    const firstKey = arr2[0];
    try {
      const savedFinanceDetails = sessionStorage.getItem('selectedFinanceDetails');
      if (savedFinanceDetails && (payMethod === "Finance" || isFinancePath)) {
        const financeDetails = JSON.parse(savedFinanceDetails);
        setOptionValue(financeDetails.optionKey);
        setValue(financeDetails.depositPercent);
        financeDetails.depositAmount = Math.ceil(parseFloat(financeDetails.depositAmount.toString()) * 100) / 100;
        setDepositAmount(financeDetails.depositAmount);
        
        if (arr.length > 0) {
          const optionIndex = arr2.indexOf(financeDetails.optionKey);
          if (optionIndex !== -1) {
            setSelectedFinanceOption(arr[optionIndex]);
          } else {
            setOptionValue(firstKey);
            setSelectedFinanceOption(arr[0]);
          }
        }

        if (validFinanceOptions[financeDetails.optionKey]) {
          setCurrentOption(validFinanceOptions[financeDetails.optionKey]);
          calculateFinanceFigures(financeDetails.optionKey, financeDetails.depositPercent);
        }
      } else {
        // if there is no saved finance option, use the default value
        setOptionValue(firstKey);
        setSelectedFinanceOption(arr[0]);
        setCurrentOption(validFinanceOptions[firstKey]);
        // calculateFinanceFigures(firstKey, value);
      }
    } catch (error) {
      console.error('Error loading saved finance option:', error);
      // Use default values if there's an error
      setOptionValue(firstKey);
      setSelectedFinanceOption(arr[0]);
      setCurrentOption(validFinanceOptions[firstKey]);
      // calculateFinanceFigures(firstKey, value);
    }
    // calculate and set slider domain
    const domain = calculateSliderDomain(firstKey);
    setSliderDomain(domain as [number, number]);
  }, [financeOptions, financePrice, registrationDetail]);

  // add a useEffect to listen to the change of optionValue, update slider domain
  useEffect(() => {
    if (optionValue) {
      const domain = calculateSliderDomain(optionValue);
      setSliderDomain(domain as [number, number]);
    }
  }, [optionValue, financePrice, maximumDeposit]);

  const calculateFinanceFigures = (
    key: string,
    depositPercent: number
  ) => {
    setSkipFinanceRecalculation(true);
    // ensure depositPercent is in the valid range
    const domain = calculateSliderDomain(key);
    const minDepositPercent = domain[0];
    const maxDepositPercent = domain[1];
    
    // since the slider is integer step, ensure depositPercent is integer
    let adjustedDepositPercent = Math.round(depositPercent);
    
    // adjust depositPercent, ensure it is in the valid range
    if (adjustedDepositPercent < minDepositPercent) {
      adjustedDepositPercent = minDepositPercent;
    } else if (adjustedDepositPercent > maxDepositPercent) {
      adjustedDepositPercent = maxDepositPercent;
    }
    
    // if depositPercent exceeds maximumDeposit, use maximumDeposit
    const _despositPercent =
      adjustedDepositPercent > maximumDeposit ? Math.floor(maximumDeposit) : adjustedDepositPercent;
    setValue(_despositPercent);
    let _depositAmount = (financePrice / 100) * _despositPercent;
    
    if (financePrice < 250 && maximumDeposit === 0) {
      _depositAmount = 0;
      setValue(0);
    }

    // ensure Credit Amount not less than minLoan
    const option = financeOptions[key];
    const minLoan = typeof option.minLoan === 'string' ? parseFloat(option.minLoan) : option.minLoan;
    let creditAmount = financePrice - _depositAmount;
    
    if (creditAmount < minLoan) {
      // if Credit Amount is less than minLoan, adjust deposit amount
      _depositAmount = Math.max(0, financePrice - minLoan);
      // recalculate depositPercent, ensure it is integer
      const newDepositPercent = Math.floor((_depositAmount / financePrice) * 100);
      setValue(newDepositPercent);
      // use the new depositPercent to recalculate deposit amount, ensure precise
      _depositAmount = (financePrice / 100) * newDepositPercent;
    }

    _depositAmount = Math.ceil(parseFloat(_depositAmount.toString()) * 100) / 100;
    setDepositAmount(_depositAmount);

    // update sessionStorage selectedFinanceDetails
    const selectedFinanceDetails = {
      optionKey: key,
      depositPercent: _despositPercent,
      depositAmount: _depositAmount
    };
    sessionStorage.setItem('selectedFinanceDetails', JSON.stringify(selectedFinanceDetails));

    if (!financeOptions[key]) return;

    const selectedProduct = {
      productId: parseInt(financeOptions[key].productId),
      productGuid: financeOptions[key].productGuid,
      name: financeOptions[key].name,
      minLoan: financeOptions[key].minLoan,
      maxLoan: financeOptions[key].maxLoan,
      months: parseInt(financeOptions[key].term),
      monthlyRate: financeOptions[key].monthlyRate,
      apr: financeOptions[key].interest,
      serviceFee: financeOptions[key].serviceFee,
      documentFee: financeOptions[key].documentFee,
      documentFeeCollectionMonth: financeOptions[key].documentFeeCollectionMonth,
      documentFeeMaximum: financeOptions[key].documentFeeMaximum,
      documentFeeMinimum: financeOptions[key].documentFeeMinimum,
      documentFeePercentage: financeOptions[key].documentFeePercentage,
      optionPeriod: financeOptions[key].optionPeriod,
      deferredPeriod: financeOptions[key].deferredPeriod,
      tag: key,
      altTag: financeOptions[key].altTag,
      settlementFee: financeOptions[key].settlementFee,
      calculationFactor: financeOptions[key].calculationFactor
    };

    const result = calculator.calculate(selectedProduct, financePrice, _depositAmount);
    // Format payment_regular_detail based on payment information
    const months = result.months;
    const initialPayments = result.initialPayments ? result.initialPayments : '0.00';
    const finalPayment = result.finalPayment ? result.finalPayment : '0.00';
    let payment_regular_detail = '';
    if (initialPayments === finalPayment) {
      payment_regular_detail = `${months} x ${POUND}${initialPayments}`;
    } else {
      payment_regular_detail = `${months - 1} x ${POUND}${initialPayments} + ${POUND}${finalPayment}`;
    }
    dispatch(
      submitFinanceOption({
        amount: financePrice,
        product: financeOptions[key].productId,
        deposit: _depositAmount,
        calculationResult: {
          apr: result.apr,
          payment_regular: result.initialPayments,
          payment_regular_detail: payment_regular_detail,
          loan_repayment: (parseFloat(result.balancePayable) + _depositAmount).toFixed(2),
          loan_cost: result.chargeForCredit,
          annual_rate: result.annualRate
        }
      })
    );

    setCurrentOption(financeOptions[key]);
  };

  const cardPaymentSubmit = (e: any) => {
    setRedirectAllowed(true);
    e.preventDefault();
    trackAddPaymentInfo(registration, parseFloat(registrationDetail?.price || '0'), 'credit_card');
    dispatch(
      submitCheckoutPayment({
        isFinance: false,
        appel,
        firstName,
        surName,
        cardNumber,
        expiry,
        cvv,
        thirdPartyQualify,
        sendCommunciation,
        agreePolicy,
        continueCallback() {
          const _purchaseValue = parseFloat(registrationDetail?.price || '0');
          trackPurchase(registration, _purchaseValue, 'credit_card', undefined, (userInfo?.user as string) || undefined);
          nextPage();
        },
      })
    );
  };

  const checkDate = (date: string) => {
    const dateStr = date.split("/");
    if (
      parseInt(dateStr[0]) > 12 ||
      parseInt(dateStr[1]) < 2022 ||
      (parseInt(dateStr[1]) === new Date().getFullYear() &&
        parseInt(dateStr[0]) < new Date().getMonth())
    ) {
      setDateExpired(true);
      return false;
    }
    setDateExpired(false);
  };

  // useEffect(() => {
  //   const beforeUnloadEvent = (event: BeforeUnloadEvent) => {
  //     if (!redirectAllowedRef.current) {
  //       event.returnValue = "Leave Site?";
  //     }
  //   };
  //   window.addEventListener("beforeunload", beforeUnloadEvent);
  //   return () => {
  //     window.removeEventListener("beforeunload", beforeUnloadEvent);
  //   };
  // }, []);

  return (
    <div className={styles.CheckoutPayment}>
      {(isGooglePaySubmitting || isApplePaySubmitting) && (
        <div style={{ position: "fixed", inset: 0, zIndex: 9999, backgroundColor: "rgba(0,0,0,0.6)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 20 }}>
          <div style={{ position: "relative", width: 72, height: 72 }}>
            <Spinner />
          </div>
          <p style={{ color: "#fff", fontSize: 18, fontWeight: 600, margin: 0 }}>Processing payment…</p>
        </div>
      )}
      <MultiStepProgress
        stepNames={progress}
        page={CURRENT_PAGE_NUMBER}
        onStepClick={(i) => {
          const urlParams = new URLSearchParams(window.location.search);
          const queryString = urlParams.toString();
          const path = progressInfos[i].path;
          navigate(queryString ? `${path}?${queryString}` : path);
        }}
      />
      <div className={styles.container}>
        <div className="page-title my-4">
          <p className="text-3xl font-bold" style={{ color: "#1678c5" }}>
            You've nearly got your New Reg!
          </p>
          <p className="text-base mt-4" style={{ color: "#868e96" }}>
            You're moments away from standing out from the crowd with your very
            own private personalised registration plate! Simply choose whether
            you'd like to pay now, or pay later with our excellent finance
            options.
          </p>
        </div>
        <div className={`${styles.platePreview} my-4 rounded-2xl`}>
          <img
            className={styles.plateImg}
            src={regPrev + "&size=" + regPrevFront}
            alt={registration}
          />
          {regPrevBack && (
            <img
              className={styles.plateImg}
              src={regPrev + "&size=" + regPrevBack}
              alt={registration}
            />
          )}
        </div>
        <form onSubmit={cardPaymentSubmit} autoComplete="on" data-form-type="payment">
          <div
            className={`${styles.priceBreakdown} my-4 md:border ${
              fetchingStatus ? "" : "md:p-4"
            } rounded-2xl relative`}
          >
            {fetchingStatus && <Spinner />}
            <div
              className={`grid xl:grid-cols-2 lg:grid-cols-1 gap-4 ${
                fetchingStatus ? "opacity-10" : ""
              }`}
            >
              <PriceBreakdown
                registration={registration}
                isFinance={payMethod === "Finance"}
              />
              <div className="p-6 bg-card-bg-grey rounded-2xl">
                <div className={`${styles.finance}`}>
                  {((showFinance && registrationDetail?.price && parseFloat(registrationDetail.price) <= 12000) ||
                    (GOOGLE_PAY_ENABLED && googlePayAvailable) || (APPLE_PAY_ENABLED && applePayAvailable)) && (
                      <>
                        <p
                          className="text-2xl font-bold"
                          style={{ color: "#1678C5" }}
                        >
                          How would you like to pay?
                        </p>
                        <div className="d-flex justify-around mt-2">
                          {/* Apple Pay first: primary payment option when available.
                              See core/applePay.ts for Apple's guideline on this. */}
                          {APPLE_PAY_ENABLED && applePayAvailable && (
                            <Radio
                              label="Apple Pay"
                              checked={payMethod === "ApplePay"}
                              onChange={(e) => { setPayMethod("ApplePay"); trackPaymentMethodSelect("apple_pay"); }}
                            />
                          )}
                          <Radio
                            label="Card"
                            checked={payMethod === "Card"}
                            onChange={(e) => { setPayMethod("Card"); trackPaymentMethodSelect("card"); }}
                          />
                          {showFinance && registrationDetail?.price && parseFloat(registrationDetail.price) <= 12000 && (
                            <Radio
                              label="Finance"
                              checked={payMethod === "Finance"}
                              onChange={(e) => { setPayMethod("Finance"); trackPaymentMethodSelect("finance"); }}
                            />
                          )}
                          {GOOGLE_PAY_ENABLED && googlePayAvailable && (
                            <Radio
                              label={
                                // Official Google Pay mark + name, per Google's brand guidelines
                                // (plain "Google Pay" text fails their production review).
                                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                                  <img src={googlePayMark} alt="" style={{ height: 24 }} />
                                  Google Pay
                                </span>
                              }
                              checked={payMethod === "GooglePay"}
                              onChange={(e) => { setPayMethod("GooglePay"); trackPaymentMethodSelect("google_pay"); }}
                            />
                          )}
                        </div>
                      </>
                    )}
                  {GOOGLE_PAY_ENABLED && payMethod === "GooglePay" && googlePayAvailable && (
                    <div className="mt-4">
                      {/* Same treatment as Apple Pay above: wallet customers finish
                          HERE without scrolling. Keep the two in step — they are a
                          family, and fixing one and not the other is how the layout
                          drifted in the first place. */}
                      {!agreePolicy && (
                        <div className="flex align-center p-3 mb-3 bg-white rounded-xl">
                          <Checkbox
                            label=""
                            checked={agreePolicy}
                            onChange={(e: React.FormEvent<HTMLInputElement>) => {
                              setAgreePolicy(!agreePolicy);
                            }}
                          />
                          <span
                            className="font-bold cursor-pointer text-xs text-left"
                            onClick={() => setAgreePolicy(!agreePolicy)}
                          >
                            I have read, and agree, to the{" "}
                            <a
                              href="/services/terms_and_conditions"
                              style={{ color: "#1e88e5" }}
                              target="_blank"
                            >
                              Terms &amp; Conditions
                            </a>{" "}
                            as well as the{" "}
                            <a href="/services/privacy_policy" style={{ color: "#1e88e5" }}>
                              privacy policy
                            </a>{" "}
                            and wish to proceed.
                          </span>
                        </div>
                      )}
                      {/* Google's own button (createButton) — it cannot be greyed out, so it
                          only appears once the T&Cs are ticked; the legal gate is unchanged. */}
                      {agreePolicy && !isGooglePaySubmitting && (
                        <GooglePayButton buttonType="pay" onClick={handleGooglePay} />
                      )}
                      {isGooglePaySubmitting && (
                        <p className="text-sm text-gray-500 mt-2 text-center">Processing Google Pay…</p>
                      )}
                      {!agreePolicy && (
                        <p className="text-xs text-gray-500 mt-2 text-center">
                          Please accept the Terms &amp; Conditions above to continue.
                        </p>
                      )}
                      {walletError && <p style={{ color: 'red', marginTop: 8, fontSize: 14 }}>{walletError}</p>}
                    </div>
                  )}
                  {APPLE_PAY_ENABLED && payMethod === "ApplePay" && applePayAvailable && (
                    <div className="mt-4">
                      {/* Wallet customers complete the order HERE, in this panel — they
                          never scroll. The whole point of a wallet is speed, and the
                          old layout put the button ~500px further down, past the price
                          breakdown and two checkboxes, disabled and unexplained.
                          The T&Cs gate is NOT bypassed: the checkbox below shares the
                          same agreePolicy state as the one at the foot of the form, so
                          ticking either satisfies both. */}
                      {!agreePolicy && (
                        <div className="flex align-center p-3 mb-3 bg-white rounded-xl">
                          <Checkbox
                            label=""
                            checked={agreePolicy}
                            onChange={(e: React.FormEvent<HTMLInputElement>) => {
                              setAgreePolicy(!agreePolicy);
                            }}
                          />
                          <span
                            className="font-bold cursor-pointer text-xs text-left"
                            onClick={() => setAgreePolicy(!agreePolicy)}
                          >
                            I have read, and agree, to the{" "}
                            <a
                              href="/services/terms_and_conditions"
                              style={{ color: "#1e88e5" }}
                              target="_blank"
                            >
                              Terms &amp; Conditions
                            </a>{" "}
                            as well as the{" "}
                            <a href="/services/privacy_policy" style={{ color: "#1e88e5" }}>
                              privacy policy
                            </a>{" "}
                            and wish to proceed.
                          </span>
                        </div>
                      )}
                      <button
                        type="button"
                        disabled={!agreePolicy || isApplePaySubmitting}
                        onClick={handleApplePay}
                        style={{
                          WebkitAppearance: "-apple-pay-button" as any,
                          width: "100%",
                          height: 48,
                          cursor: !agreePolicy || isApplePaySubmitting ? "not-allowed" : "pointer",
                          opacity: !agreePolicy || isApplePaySubmitting ? 0.4 : 1,
                          border: "none",
                          display: "block",
                        }}
                        aria-label="Pay with Apple Pay"
                      />
                      {/* Say WHY it is disabled. A greyed-out button with no reason is
                          what made this look broken during testing. */}
                      {!agreePolicy && (
                        <p className="text-xs text-gray-500 mt-2 text-center">
                          Please accept the Terms &amp; Conditions above to continue.
                        </p>
                      )}
                      {walletError && <p style={{ color: 'red', marginTop: 8, fontSize: 14 }}>{walletError}</p>}
                    </div>
                  )}
                  {payMethod === "Card" && (
                    <div className="mt-4">
                      <p className="text-base font-bold mt-4">
                        Pay by debit or credit card:
                      </p>
                      <div className="grid grid-cols-5 gap-4 mt-4">
                        <div className="">
                          <Select
                            label="Title"
                            onChange={(
                              e: React.FormEvent<HTMLSelectElement>
                            ) => {
                              setAppel(e.currentTarget.value);
                            }}
                            value={appel}
                            options={appellation}
                          />
                        </div>
                        <div className="col-span-2">
                          <TextField
                            label="First Name"
                            value={firstName}
                            name="forename"
                            aria-label="Cardholder first name"
                            data-dashlane-label="cardholderName"
                            errorMsg={
                              !firstName ? "Please enter your firstname." : ""
                            }
                            onChange={(e) =>
                              setFirstName(e.currentTarget.value)
                            }
                            autocomplete="cc-given-name"
                          />
                        </div>
                        <div className="col-span-2">
                          <TextField
                            label="Surname"
                            errorMsg={
                              !surName ? "Please enter your surname." : ""
                            }
                            name="surname"
                            value={surName}
                            aria-label="Cardholder surname"
                            data-dashlane-label="cardholderName"
                            onChange={(e) => setSurName(e.currentTarget.value)}
                            autocomplete="cc-family-name"
                          />
                        </div>
                      </div>
                      <div
                        className={`w-full mt-4 grid ${
                          ["MasterCard", "VisaCard"].includes(cardType)
                            ? "grid-cols-4 md:grid-cols-5 gap-4"
                            : "grid-cols-4"
                        }`}
                      >
                        <div className="col-span-4 md:col-span-3">
                          <TextField
                            label="Card Number:"
                            value={cardNumber}
                            name="cardnumber"
                            type="tel"
                            inputMode="numeric"
                            placeholder="1234 5678 9012 3456"
                            aria-label="Card number"
                            data-dashlane-label="cardNumber"
                            icon="credit_card"
                            autocomplete="cc-number"
                            id="cardnumber"
                            errorMsg={
                              cardType === "AmexCard"
                                ? NO_AMEX
                                : cardNumber !== "" && !cardType
                                ? "Please enter correct card number."
                                : ""
                            }
                            onChange={(e) => {
                              const cType = checkCardType(
                                e.currentTarget.value.slice(0, 4)
                              );
                              if (
                                cType === "AmexCard" &&
                                e.currentTarget.value.length > 15
                              )
                                return;
                              if (
                                (cType === "VisaCard" ||
                                  cType === "MasterCard") &&
                                e.currentTarget.value.length > 16
                              )
                                return;
                              if (
                                cType === "MaestroCard" &&
                                e.currentTarget.value.length > 19
                              )
                                return;
                              setCardType(cType);
                              setCardNumber(e.currentTarget.value);
                            }}
                          />
                        </div>
                        {["MasterCard", "VisaCard"].includes(cardType) && (
                          <div className="col-span-4 md:col-span-2">
                            <Select
                              label="Card type"
                              onChange={(
                                e: React.FormEvent<HTMLSelectElement>
                              ) => {
                                setCardUseType(e.currentTarget.value);
                              }}
                              value={cardUseType}
                              options={["Credit", "Debit"]}
                            />
                          </div>
                        )}
                      </div>
                      <div className={`grid gap-4 mt-4 ${styles.cardExpiryRow}`}>
                        <TextField
                          label="Expiry (MMYYYY)"
                          value={expiry}
                          type="tel"
                          inputMode="numeric"
                          placeholder="MM/YYYY"
                          aria-label="Card expiry date"
                          data-dashlane-label="cardExpirationDate"
                          icon="update"
                          name="cardexpiry"
                          id="cardexpiry"
                          errorMsg={
                            expiry !== "" && dateExpiried
                              ? "Invalid date or date has expired."
                              : ""
                          }
                          autocomplete="cc-exp"
                          onChange={(e) => {
                            let raw = e.currentTarget.value;
                            // Password-manager autofill: value jumps to MM/YY in one event.
                            // Expand 2-digit year to 4-digit (20YY) so validation passes.
                            if (
                              /^\d{2}\/\d{2}$/.test(raw) &&
                              raw.length - expiry.length > 1
                            ) {
                              raw = raw.slice(0, 3) + "20" + raw.slice(3);
                            }
                            if (raw.length > 7) {
                              return;
                            }
                            let value: string[] | string = raw
                              .replace("/", "")
                              .split("");
                            if (value.length >= 3) {
                              value.splice(2, 0, "/");
                            }
                            value = value.join("");
                            checkDate(value);
                            if (!/^[0-9]{2}\/[0-9]{4}$/.test(value)) {
                              setDateExpired(true);
                            } else {
                              setDateExpired(false);
                            }
                            setExpiry(value);
                          }}
                        />
                        <TextField
                          label="CVV2"
                          value={cvv}
                          type="tel"
                          inputMode="numeric"
                          placeholder="CVV"
                          aria-label="Card security code"
                          data-dashlane-label="cvv"
                          icon="payments"
                          name="signature_strip_number"
                          onChange={(e) => {
                            if (e.currentTarget.value.length > 4) return;
                            if (!/^[0-9]*$/.test(e.currentTarget.value)) return;
                            setCvv(e.currentTarget.value);
                          }}
                          autocomplete="cc-csc"
                          onFocus={() => setFlipped(true)}
                          onBlur={() => setFlipped(false)}
                        />
                      </div>
                      <div
                        className={`${styles.cardPreview} flex justify-center mt-4`}
                      >
                        <div
                          className={`${styles.jpCard} ${
                            flipped ? styles.jpCardFlipped : ""
                          }`}
                        >
                          <div
                            className={`rounded-xl ${
                              cardType ? styles.cardBgImg : ""
                            } ${styles.cardStyle}`}
                            style={{
                              backgroundColor: cardBgColor
                                ? cardBgColor
                                : "#DDD",
                            }}
                          >
                            {!flipped ? (
                              <div
                                className={`${styles.cardFront} w-full h-full`}
                              >
                                <div className={`${styles.cardShiny}`}></div>
                                <div
                                  className="absolute"
                                  style={{
                                    width: 60,
                                    height: 36,
                                    top: 10,
                                    right: 10,
                                  }}
                                >
                                  <img src={cardIcon} alt={cardType} />
                                </div>
                                <div className={`${styles.cardNumber} mt-4`}>
                                  {cardNumber
                                    ? cardNumberFormat(cardNumber)
                                    : "**** **** **** ****"}
                                </div>
                                <div className="md:mt-6 mt-4 flex justify-around">
                                  <div className={`${styles.cardName}`}>
                                    {firstName + " " + surName}
                                  </div>
                                  <div className={`${styles.cardExpiry}`}>
                                    {expiry}
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <div
                                className={`py-8 w-full h-full ${styles.cardBack}`}
                              >
                                <div className={`${styles.cardBar}`}></div>
                                <div
                                  className={`${styles.cardCvv} md:mt-6 mt-3 relative`}
                                >
                                  <p
                                    className={`md:ml-4 ml-2 text-white w-fit`}
                                    style={{ transform: "rotateY(180deg)" }}
                                  >
                                    {cvv ? cvv : "****"}
                                  </p>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="mt-8 text-sm">
                        Please Note: New Reg cannot process your order if the
                        credit/debit card details or address supplied are
                        incorrect. Please make sure that the details correspond
                        with your credit/debit card.
                      </div>
                    </div>
                  )}
                  {payMethod === "Finance" && (
                    <div className="mt-4">
                      <p className="text-base font-bold">
                        Choose deposit amount:
                      </p>
                      {forceDepositMax && maximumDeposit > 0 ? (
                        <p className="text-sm bg-amber-200 p-4 mt-4">
                          Our minimum financeable figure is £250, with this
                          transaction the{" "}
                          <span className="font-bold">
                            maximum allowable deposit is {maximumDeposit}{" "}
                            percent.
                          </span>
                        </p>
                      ) : (
                        <></>
                      )}
                      <div className="my-8">
                        <Slider
                          value={value}
                          domain={sliderDomain}
                          onValueChange={(value: Array<number>) => {
                            calculateFinanceFigures(optionValue, value[0]);
                          }}
                        />
                      </div>
                      <div className="mt-4">
                        <Select
                          onChange={(e: React.FormEvent<HTMLSelectElement>) => {
                            const index = e.currentTarget.selectedIndex - 1;
                            setOptionValue(optionKeys[index]);
                            setCurrentOption(financeOptions[optionKeys[index]]);
                            setSelectedFinanceOption(options[index]);
                            
                            // update sessionStorage selectedFinanceDetails
                            const selectedFinanceDetails = {
                              optionKey: optionKeys[index],
                              depositPercent: value,
                              depositAmount: _depositAmount
                            };
                            sessionStorage.setItem('selectedFinanceDetails', JSON.stringify(selectedFinanceDetails));
                            
                            calculateFinanceFigures(optionKeys[index], value);
                          }}
                          value={selectedFinanceOption}
                          ariaLabel="Finance option"
                          options={options}
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 md:gap-4">
                        <div>
                          <p className="text-sm flex align-center justify-between border-b py-4 px-1">
                            <span>Cash Price</span>
                            <span className="">
                              {POUND}
                              {nf.format(financePrice)}
                            </span>
                          </p>
                          <p className="text-sm flex align-center justify-between border-b py-4 px-1">
                            <span>Desposit</span>
                            <span className="">
                              {POUND}
                              {_depositAmount.toFixed(2)}
                            </span>
                          </p>
                          <p className="text-sm flex align-center justify-between border-b py-4 px-1">
                            <span>Credit Amount</span>
                            <span className="">
                              {POUND}
                              {nf.format(financePrice - _depositAmount)}
                            </span>
                          </p>
                          <p className="text-sm flex align-center justify-between border-b py-4 px-1">
                            <span>Monthly Payments</span>
                            <span className="">
                              {creditInformation?.payment_regular_detail}
                            </span>
                          </p>
                        </div>
                        <div>
                          <p className="text-sm flex align-center justify-between border-b py-4 px-1">
                            <span>Representative APR</span>
                            <span className="">{creditInformation?.apr}%</span>
                          </p>
                          {creditInformation?.annual_rate  && (
                            <p className="text-sm flex align-center justify-between border-b py-4 px-1">
                              <span>Annual Rate of Interest Fixed</span>
                              <span className="">
                                {creditInformation?.annual_rate}%
                              </span>
                            </p>
                          )}
                          <p className="text-sm flex align-center justify-between border-b py-4 px-1">
                            <span>Total Payable</span>
                            <span className="">
                              {POUND}
                              {creditInformation.loan_repayment}
                            </span>
                          </p>
                        </div>
                      </div>
                      <p className="text-xs mt-4 border-b py-4">
                        * The figures displayed are a representative example. 0%
                        APR Finance available on select plates only. Subject to
                        credit application and approval.
                      </p>
                      <Criteria />
                    </div>
                  )}
                  <p className="font-bold mt-4 text-center">
                    Need help?{" "}
                    <a href="tel:01772 566400" style={{ color: "#2fa1ff" }}>
                      Call us on: 01772 566400
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-4 md:p-2 md:border rounded-2xl md:text-sm text-xs">
            <div className="flex align-center justify-center p-4 bg-card-bg-grey rounded-2xl">
              <Checkbox
                label=""
                checked={keepUpdate}
                onChange={(e: React.FormEvent<HTMLInputElement>) => {
                  setKeepUpdate(!keepUpdate);
                }}
              />
              <span
                className="font-bold cursor-pointer text-left"
                onClick={() => setKeepUpdate(!keepUpdate)}
              >
                Keep me updated on New Reg news, events and offers.
              </span>
            </div>
          </div>
          <div className="mt-4">
            <div className="flex align-center justify-center p-4 bg-card-bg-grey rounded-2xl">
              <Checkbox
                label=""
                checked={agreePolicy}
                onChange={(e: React.FormEvent<HTMLInputElement>) => {
                  setAgreePolicy(!agreePolicy);
                }}
              />
              <span
                className="font-bold cursor-pointer md:text-sm text-xs text-left"
                onClick={() => setAgreePolicy(!agreePolicy)}
              >
                I have read, and agree, to the{" "}
                <a
                  href="/services/terms_and_conditions"
                  style={{ color: "#1e88e5" }}
                  target="_blank"
                >
                  Terms &amp; Conditions
                </a>{" "}
                as well as the{" "}
                <a href="/services/privacy_policy" style={{ color: "#1e88e5" }}>
                  privacy policy
                </a>{" "}
                and wish to proceed.
              </span>
            </div>
          </div>
          <div className="mt-4 mb-8 flex flex-wrap justify-evenly md:grid-cols-2 sm:grid-cols-1 gap-8">
            <ButtonRound
              text="Previous Page"
              type="primary"
              className="previous-page-btn"
              onClick={prevPage}
              style={{ height: 45, width: 320 }}
            />
            {payMethod === "Card" && (
              <ButtonRound
                text="Submit Payment"
                type="success"
                className="submit-payment-card-btn"
                disabled={
                  !agreePolicy ||
                  !firstName ||
                  !surName ||
                  !cardNumber ||
                  !cardType ||
                  cardType === "AmexCard" ||
                  !expiry ||
                  !cvv ||
                  dateExpiried ||
                  isSubmitting
                }
                buttonType="submit"
                style={{ height: 45, width: 320 }}
              />
            )}
            {payMethod === "Finance" && (
              <ButtonRound
                text="Continue To Credit Application"
                type="success"
                className="submit-payment-finance-btn"
                disabled={!agreePolicy || isSubmitting}
                style={{ height: 45, width: 320 }}
                onClick={() => {
                  setRedirectAllowed(true);
                  
                  // save current finance details to session storage
                  const selectedFinanceDetails = {
                    optionKey: optionValue,
                    depositPercent: value,
                    depositAmount: _depositAmount
                  };
                  sessionStorage.setItem('selectedFinanceDetails', JSON.stringify(selectedFinanceDetails));
                  
                  trackAddPaymentInfo(registration, financePrice, 'finance');
                  dispatch(
                    submitCheckoutPayment({
                      isFinance: true,
                      appel,
                      firstName,
                      surName,
                      cardNumber,
                      expiry,
                      cvv,
                      thirdPartyQualify,
                      sendCommunciation,
                      agreePolicy,
                      // add finance product id and guid
                      finance_product_id: financeOptions[optionValue]?.productId,
                      finance_product_guid: financeOptions[optionValue]?.productGuid,
                      // add deposit amount
                      deposit: _depositAmount,
                      continueCallback(v12Url?: string) {
                        trackPurchase(registration, financePrice, 'finance', undefined, (userInfo?.user as string) || undefined);
                        nextPage(v12Url);
                      },
                    })
                  );
                }}
              />
            )}
            {/* The Google Pay button used to live here, at the foot of the form.
                It now sits inside the "How would you like to pay?" panel, matching
                Apple Pay -- see that block above. */}
            {/* The Apple Pay button used to live here, at the foot of the form.
                It now sits inside the "How would you like to pay?" panel so wallet
                customers complete without scrolling — see that block above. */}
            <ThreeDAuthDialog nextPage={nextPage}></ThreeDAuthDialog>
          </div>
        </form>
      </div>
    </div>
  );
};

export { CheckoutPayment, CheckoutPayment as default };
